import { PendingQuestions, Case, LocalizedText } from "../apis/API";
import { getCurrentTimeInSeconds } from "./UtilityFunctions";

/** Maximum characters for any participant text input (matches Android's CappedTextField). */
export const MAX_TEXT_LENGTH = 1000;

/**
 * Resolve localized text from a `[LocalizedText]` list, preferring the entry
 * that matches the participant's `navigator.language`, then falling back to the
 * first entry that has any text. Returns `undefined` when nothing usable is
 * present, so callers can apply their own fallback.
 *
 * Single source of truth for the locale matching previously duplicated across
 * the question components (question text) and the wizard (semantic-group label).
 */
export const resolveLocalizedText = (
  localization: Array<LocalizedText | null> | null | undefined,
): string | undefined => resolveLocalizedEntry(localization)?.text;

/**
 * The same resolution, but it also reports WHICH locale the text came from —
 * WCAG 2.1 SC 3.1.2 Language of Parts.
 *
 * The fallback branch can return text in a language the document does not
 * declare: a participant browsing in Spanish, given a question the server only
 * localized into English, gets English inside a page declaring `lang="es"`, and
 * a screen reader pronounces it with a Spanish voice profile. Selection
 * behaviour is deliberately unchanged — showing the fallback is better than
 * showing nothing. What changes is that callers can now say what language it
 * is, via {@link langAttribute}.
 */
export const resolveLocalizedEntry = (
  localization: Array<LocalizedText | null> | null | undefined,
): { text: string; locale: string | null } | undefined => {
  // The locale match also requires non-empty text, so a locale-matched entry
  // with empty text doesn't short-circuit past an entry that actually has text.
  const exact = localization?.find(
    (l) => l?.locale && navigator.language.includes(l.locale) && l.text,
  );
  if (exact?.text) return { text: exact.text, locale: exact.locale ?? null };
  const fallback = localization?.find((l) => l?.text);
  if (fallback?.text) {
    return { text: fallback.text, locale: fallback.locale ?? null };
  }
  return undefined;
};

/**
 * The `lang` attribute for a resolved entry, or `undefined` when the text is
 * already in the document's language (in which case marking it would be noise).
 *
 * Compares the primary subtag only: a document declaring `es` and text tagged
 * `es-MX` are the same language for pronunciation purposes.
 */
export const langAttribute = (
  locale: string | null | undefined,
): string | undefined => {
  if (!locale) return undefined;
  const docLang = document.documentElement.lang || "en";
  const base = (tag: string) => tag.split("-")[0].toLowerCase();
  return base(locale) === base(docLang) ? undefined : locale;
};

/**
 * Validates if questions are currently due (within the scheduled time window)
 * - Checks that pendingQuestions exists and has required fields (promptId, startTime, answersValidUntil)
 * - Verifies that current time falls within the startTime and answersValidUntil window
 */
export const areQuestionsDue = (
  pendingQuestions: PendingQuestions | null | undefined,
): boolean => {
  if (
    !pendingQuestions?.promptId ||
    !pendingQuestions.startTime ||
    !pendingQuestions.answersValidUntil
  ) {
    return false;
  }
  const currentTime = getCurrentTimeInSeconds();
  return (
    currentTime >= pendingQuestions.startTime &&
    currentTime <= pendingQuestions.answersValidUntil
  );
};

/**
 * Validates if the "Answer Now" button should be shown for self-triggered questions
 * - Checks that the self-triggered questions feature flag is enabled
 * - Verifies that no questions are currently due (inverse of areQuestionsDue)
 * - Validates that the participant has a valid question assignment with a questionGroupId
 * - Ensures that the question assignment has at least one question available
 */
export const canAnswerQuestionsNow = (
  isSelfTriggeredQuestionsEnabled: boolean,
  participantCase: Case | null | undefined,
  areQuestionsDueNow: boolean,
): boolean => {
  return (
    isSelfTriggeredQuestionsEnabled &&
    !areQuestionsDueNow &&
    participantCase?.questionAssignment?.questionGroupId != null &&
    (participantCase?.questionAssignment?.agencyQuestionGroup
      ?.localizedParticipantQuestions?.length ?? 0) > 0
  );
};
